import {Button, Stack, Flash} from '@primer/react'
import {Dialog} from '@primer/react/deprecated'
import {StopIcon} from '@primer/octicons-react'
import {useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {useClosePullRequestMutation} from '../../hooks/mutations/use-close-pull-request-mutation'
import {useAnalytics} from '@github-ui/use-analytics'

export function ClosePullRequestSection() {
  const [dialogState, setDialogState] = useState<'closed' | 'open'>('closed')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const closePullRequestRef = useRef<HTMLButtonElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  const {sendAnalyticsEvent} = useAnalytics()

  const onOpenCloseDialog = () => {
    setDialogState('open')
    setErrorMessage(null)
  }

  const {mutate: closePullRequestMutation, isPending} = useClosePullRequestMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
      setTimeout(() => announce('Failed to close the pull request'), 1000)
    },
    onSuccess: () => {
      setDialogState('closed')
      setTimeout(() => announce('The pull request was successfully closed.'), 1000)
      sendAnalyticsEvent('close_pull_request_section.close', 'MERGEBOX_CLOSE_PULL_REQUEST_SECTION_CLOSE_BUTTON')
    },
  })

  const handleClosePullRequest = async () => {
    if (isPending) return
    setErrorMessage(null)

    closePullRequestMutation()
  }

  return (
    <>
      <Stack direction="horizontal" align="baseline" className="text-small flex-justify-start tmp-px-3 py-1" gap="none">
        <span className="fgColor-muted pr-1">Not planning to merge?</span>
        <Button
          variant="link"
          className="Link--muted Link--inTextBlock text-normal"
          onClick={onOpenCloseDialog}
          ref={closePullRequestRef}
        >
          Close pull request
        </Button>
      </Stack>

      <Dialog
        aria-labelledby="close-pull-request-dialog-title"
        isOpen={dialogState === 'open'}
        returnFocusRef={closePullRequestRef}
        initialFocusRef={cancelRef}
        onDismiss={() => {
          if (!isPending) setDialogState('closed')
        }}
      >
        <Dialog.Header id="close-pull-request-dialog-title">Close this pull request?</Dialog.Header>
        <div className="tmp-p-3">
          <Flash variant="warning">This pull request will be closed. You can reopen it later if needed.</Flash>

          {errorMessage && (
            <Flash className="my-2" variant="danger">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}

          <div className="d-flex flex-justify-end tmp-mt-3">
            <Button
              className="mr-1"
              disabled={isPending}
              variant="invisible"
              ref={cancelRef}
              onClick={() => {
                if (!isPending) {
                  setDialogState('closed')
                  requestAnimationFrame(() => closePullRequestRef.current?.focus())
                }
              }}
            >
              Cancel
            </Button>
            <Button
              loading={isPending}
              loadingAnnouncement="Closing pull request"
              onClick={handleClosePullRequest}
              variant="danger"
            >
              Close pull request
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}
